import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';

import { PERMISSION } from 'src/features/auth';
import { commonMessages } from 'src/features/auth/utils';
import { MEMBERSHIP_USER_ACCESS_LEVEL, MembershipCloudUser } from 'src/features/membership/types';
import { identifierTypeBadgeOptions } from 'src/features/membership/utils/constants';
import { membershipMessages } from 'src/features/membership/utils/messages';
import { useListMembershipCloudUsers } from 'src/features/membershipCloudUsers/api/useListMembershipCloudUsers';
import CellMembershipCloudUserActions from 'src/features/membershipCloudUsers/components/cells/CellMembershipCloudUserActions';
import CreateMembershipCloudUserFormModal from 'src/features/membershipCloudUsers/components/forms/CreateMembershipCloudUserFormModal';
import { MEMBERSHIP_USER_ACCESS_LEVEL_BADGES } from 'src/features/membershipCloudUsers/utils/constants';
import { membershipUsersMessages } from 'src/features/membershipCloudUsers/utils/messages';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import { CellBoolean } from 'src/registry/components/table/cells/CellBoolean';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { generalMessages } from 'src/utils/messages';

type MembershipCloudUsersTableProps = {
    membershipId: string;
    isClient?: boolean;
    accessLevel?: MEMBERSHIP_USER_ACCESS_LEVEL;
};

const MembershipCloudUsersTable = ({ membershipId, isClient = false, accessLevel }: MembershipCloudUsersTableProps) => {
    const { formatMessage } = useIntl();
    const { parkingId } = useParking();

    const { queryPaginationOptions, ...pagination } = usePagination();
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'search' });
    const { querySortingOptions, ...sorting } = useSorting();

    const {
        data: membershipCloudUsers,
        isError,
        isLoading,
    } = useListMembershipCloudUsers({
        parkingId,
        membershipId,
        ...queryPaginationOptions,
        ...querySortingOptions,
        ...querySearchOptions,
    });

    const permissionManageMemberships = useHasPermissions([PERMISSION.MANAGE_MEMBERSHIPS]);
    const showCreateCloudUser =
        (permissionManageMemberships && membershipId && !isClient) ||
        (isClient && accessLevel && accessLevel === MEMBERSHIP_USER_ACCESS_LEVEL.ADMIN);

    const columnHelper = createColumnHelper<MembershipCloudUser>();
    const columns = [
        columnHelper.accessor('cloud_user.name', {
            header: DataTableColumnHeader,
            enableSorting: true,
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('cloud_user.email', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            meta: {
                title: formatMessage(commonMessages.email),
                badgeOptions: identifierTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('access_level', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(membershipUsersMessages.accessLevel),
                badgeOptions: MEMBERSHIP_USER_ACCESS_LEVEL_BADGES,
            },
        }),
        columnHelper.accessor('cloud_user.is_active', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBoolean,
            meta: {
                title: formatMessage(membershipMessages.isActive),
            },
        }),
        ...(showCreateCloudUser
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (ctx) => {
                          return <CellMembershipCloudUserActions {...ctx} row={ctx.row} membershipId={membershipId} />;
                      },
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<MembershipCloudUser, any>
            columns={columns}
            data={membershipCloudUsers}
            pagination={pagination}
            sorting={sorting}
            search={search}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(membershipUsersMessages.membershipUsers),
                entryName: formatMessage(membershipUsersMessages.membershipUser).toLowerCase(),
                entryNames: formatMessage(membershipUsersMessages.membershipUsersTableBadge).toLowerCase(),
            }}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.cloud_user.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(showCreateCloudUser && {
                    newEntry: () => <CreateMembershipCloudUserFormModal membershipId={membershipId} />,
                }),
            }}
        />
    );
};

export default MembershipCloudUsersTable;
